import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trophy, ArrowLeft, RefreshCw, Flame, Printer, Medal } from "lucide-react";

const API = '/api';

export default function EventLeaderboards() {
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchData();
  }, [eventId]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchData, 15000); // Refresh every 15 seconds
    }
    return () => clearInterval(interval);
  }, [autoRefresh, eventId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/event-leaderboards/${eventId}`);
      setData(res.data);
    } catch (error) {
      toast.error("Failed to fetch leaderboards");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getRankBadge = (rank) => {
    return <span className="text-slate-800 font-bold">{rank}</span>;
  };

  const getRankClass = (rank) => {
    if (rank === 1) return "bg-amber-50 border-l-4 border-amber-400";
    if (rank === 2) return "bg-slate-50 border-l-4 border-slate-300";
    if (rank === 3) return "bg-orange-50 border-l-4 border-orange-300";
    return "";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="event-leaderboards-page" className="space-y-6">
      {/* Header */}
      <div className="print:hidden">
        <Link to={`/matches/${eventId}`} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2">
          <ArrowLeft size={14} /> Back to Matches
        </Link>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight flex items-center gap-3">
              <Trophy className="text-amber-500" size={36} />
              {data?.event?.name} - Leaderboards
            </h1>
            <div className="flex items-center gap-2 text-slate-500 mt-1">
              <RefreshCw size={14} className={autoRefresh ? "animate-spin" : ""} />
              <span>Auto-refreshing every 15s</span>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              {autoRefresh ? "Pause" : "Resume"} Auto-Refresh
            </Button>
            <Button variant="outline" onClick={handlePrint}>
              <Printer size={18} className="mr-2" /> Print All
            </Button>
          </div>
        </div>
      </div>

      {/* Print header */}
      <div className="hidden print:block text-center mb-8">
        <h1 className="text-2xl font-bold">NMLRA - {data?.event?.name}</h1>
        <p className="text-slate-500">Leaderboards - {new Date().toLocaleString()}</p>
      </div>

      {/* Leaderboards Grid */}
      {data?.match_leaderboards?.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Trophy className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">No matches or scores recorded yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6 print:grid-cols-1">
          {data?.match_leaderboards?.map(({ match, leaderboard, total_shooters }) => (
            <Card key={match.id} className="print:break-inside-avoid overflow-hidden">
              <CardHeader className="pb-2 bg-gradient-to-r from-slate-50 to-white">
                <CardTitle className="font-serif text-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Medal className="text-amber-500" size={20} />
                    {match.name}
                  </div>
                  <span className="text-sm font-normal text-slate-500">
                    {match.shots_per_round} shots • ${match.entry_fee}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                {leaderboard.length === 0 ? (
                  <p className="text-slate-400 text-center py-4">No scores yet</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-slate-200">
                        <th className="text-left py-2 w-16 font-semibold text-slate-600">Rank</th>
                        <th className="text-left py-2 font-semibold text-slate-600">Shooter</th>
                        <th className="text-center py-2 font-semibold text-slate-600">Score</th>
                        <th className="text-center py-2 font-semibold text-slate-600">
                          <span className="flex items-center justify-center gap-1">
                            <Flame size={14} className="text-orange-500" />
                            Run
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.map((entry) => (
                        <tr 
                          key={entry.shooter_id || entry.team_name} 
                          className={`border-b border-slate-100 ${getRankClass(entry.rank)}`}
                        >
                          <td className="py-2 font-bold text-lg">
                            {getRankBadge(entry.rank)}
                          </td>
                          <td className="py-2 font-medium">
                            {match.is_team_event ? entry.team_name : entry.shooter_name}
                            {entry.rounds_shot > 1 && (
                              <span className="text-xs text-slate-400 ml-1">
                                ({entry.rounds_shot} rounds)
                              </span>
                            )}
                          </td>
                          <td className="py-2 text-center">
                            <span className="font-mono font-bold text-lg text-green-700">
                              {entry.best_score}
                            </span>
                            <span className="text-slate-400">/{match.shots_per_round}</span>
                          </td>
                          <td className="py-2 text-center font-mono font-bold text-orange-600">
                            {entry.best_streak || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="text-xs text-slate-400 mt-3 text-right border-t pt-2">
                  {total_shooters} shooter{total_shooters !== 1 ? 's' : ''} • Ties broken by longest run
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          .print\\:grid-cols-1 { grid-template-columns: 1fr !important; }
          .print\\:break-inside-avoid { break-inside: avoid; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
